import React, { useRef, useState, useCallback } from 'react';
import { Mail, Clock, Wrench, Plus } from 'lucide-react';

// Lightweight Visio/Miro-style canvas: draggable step boxes connected by
// pointed arrows. No external deps — absolute-positioned nodes on a relative
// canvas + an SVG overlay for the arrows. Edits the same step data the list
// builder uses (pos_x/pos_y persist node positions; order drives the arrow
// chain).

const NODE_W = 190;
const NODE_H = 76;
const TYPE = {
  email: { icon: Mail, color: '#3d9ff9', label: 'Email' },
  timer: { icon: Clock, color: '#f59e0b', label: 'Timer' },
  external: { icon: Wrench, color: '#10b981', label: 'External' },
};

// Auto-layout a step that has no saved position: stagger down-right by order.
const posOf = (s, i) => ({
  x: s.pos_x != null ? s.pos_x : 60 + (i % 2) * 70,
  y: s.pos_y != null ? s.pos_y : 30 + i * (NODE_H + 46),
});

const WorkflowCanvas = ({ steps, onChange, selectedKey, onSelect, onAddStep }) => {
  const canvasRef = useRef(null);
  const drag = useRef(null);

  const onMouseDown = useCallback((e, i) => {
    e.stopPropagation();
    const p = posOf(steps[i], i);
    drag.current = { i, startX: e.clientX, startY: e.clientY, ox: p.x, oy: p.y, moved: false };
  }, [steps]);

  const onMouseMove = useCallback((e) => {
    if (!drag.current) return;
    const { i, startX, startY, ox, oy } = drag.current;
    const dx = e.clientX - startX, dy = e.clientY - startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.current.moved = true;
    const steps2 = [...steps];
    steps2[i] = { ...steps2[i], pos_x: Math.max(0, ox + dx), pos_y: Math.max(0, oy + dy) };
    onChange(steps2);
  }, [steps, onChange]);

  const onMouseUp = useCallback((e, i) => {
    if (drag.current && !drag.current.moved) onSelect(steps[i].key);
    drag.current = null;
  }, [steps, onSelect]);

  const positions = steps.map((s, i) => posOf(s, i));
  // Canvas height grows to fit nodes.
  const maxY = Math.max(260, ...positions.map((p) => p.y + NODE_H + 60));

  return (
    <div className="relative rounded-xl border border-slate-200 bg-slate-50 overflow-auto"
         style={{ height: 460 }}
         onMouseMove={onMouseMove}
         onMouseLeave={() => { drag.current = null; }}>
      <div ref={canvasRef} className="relative" style={{ minWidth: 560, height: maxY }}>
        {/* arrows: chain consecutive steps by order */}
        <svg className="absolute inset-0 pointer-events-none" width="100%" height={maxY}>
          <defs>
            <marker id="wf-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3"
                    orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L8,3 L0,6 Z" fill="#94a3b8" />
            </marker>
          </defs>
          {steps.slice(0, -1).map((s, i) => {
            const a = positions[i], b = positions[i + 1];
            const x1 = a.x + NODE_W / 2, y1 = a.y + NODE_H;
            const x2 = b.x + NODE_W / 2, y2 = b.y;
            const my = (y1 + y2) / 2;
            return (
              <path key={s.key}
                d={`M${x1},${y1} C${x1},${my} ${x2},${my} ${x2},${y2 - 2}`}
                stroke="#94a3b8" strokeWidth="1.6" fill="none" markerEnd="url(#wf-arrow)" />
            );
          })}
        </svg>

        {/* nodes */}
        {steps.map((s, i) => {
          const p = positions[i];
          const M = TYPE[s.type] || TYPE.email;
          const Icon = M.icon;
          const sel = s.key === selectedKey;
          return (
            <div key={s.key}
              onMouseDown={(e) => onMouseDown(e, i)}
              onMouseUp={(e) => onMouseUp(e, i)}
              className={`absolute bg-white rounded-xl border-2 px-3 py-2 cursor-move shadow-sm select-none ${sel ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}
              style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H }}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-md" style={{ background: M.color + '22' }}>
                  <Icon className="h-3 w-3" style={{ color: M.color }} />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: M.color }}>{M.label}</span>
                <span className="ml-auto text-[10px] text-slate-400">#{i + 1}</span>
              </div>
              <div className="text-[12px] font-medium text-slate-700 leading-tight line-clamp-2">
                {s.label || <span className="text-slate-400 italic">Untitled step</span>}
              </div>
              {s.wait_days > 0 && (
                <div className="text-[10px] text-slate-400 mt-0.5">wait {s.wait_days}d</div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={onAddStep}
        className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium px-3 py-1.5 hover:bg-blue-700 shadow">
        <Plus className="h-3.5 w-3.5" /> Add step
      </button>
    </div>
  );
};

export default WorkflowCanvas;
